
import React, { useRef, useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import SelectStudentModal from "../AdminAttendanceEntry/SelectStudentModal";
import SelectStudentFeeModal from "../FeeStudentModal/SelectStudentFeeModal";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { FaPlus } from "react-icons/fa";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { ApiUrl } from "../../../ApiUrl";
import jsPDF from "jspdf";
import "jspdf-autotable";

const safeText = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(" ").trim();
  }

  if (value && typeof value === "object") {
    return Object.values(value).filter(Boolean).join(" ").trim();
  }

  return value === null || value === undefined ? "" : String(value).trim();
};

const formatReceiptDate = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return safeText(value);
  }

  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const isOtherChargeElement = (item) => {
  const label = safeText(item?.element_name || "").toUpperCase().replace(/\s+/g, " ").trim();
  // Match both frontend label variants and backend API variants
  return [
    "LATE FEE",
    "LATE FEES",
    "READMISSION",
    "RE ADMISSION",
    "DISCOUNT",
    "CHEQUE BOUNCE",
    "CHECK BOUNCE",
  ].some((charge) => label.includes(charge));
};

const isMainFeeElement = (item) => {
  if (!item) return false;
  const label = safeText(item.element_name || "").toUpperCase();
  return !isOtherChargeElement(item);
};

const isDiscountElement = (item) => {
  const label = safeText(item?.element_name || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  return label === "DISCOUNT";
};

const feePeriodOrder = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
  yearly: 13,
};

const getFeePeriodSortKey = (period) => {
  const normalizedPeriod = safeText(period).toLowerCase().trim();
  const isYearlyPeriod = /\s*\(yearly\)$/.test(normalizedPeriod);
  const basePeriod = normalizedPeriod
    .replace(/\s*\(yearly\)$/, "")
    .trim();

  return {
    order: feePeriodOrder[basePeriod] || Number.MAX_SAFE_INTEGER,
    yearly: isYearlyPeriod ? 1 : 0,
  };
};

const amountToWords = (value) => {
  const amount = Math.round(Number(value) || 0);
  if (amount === 0) return "Zero Rupees Only";

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const toWords = (num) => {
    if (num < 20) return ones[num];
    if (num < 100) return `${tens[Math.floor(num / 10)]} ${ones[num % 10]}`.trim();
    if (num < 1000) return `${ones[Math.floor(num / 100)]} Hundred ${toWords(num % 100)}`.trim();
    if (num < 100000) return `${toWords(Math.floor(num / 1000))} Thousand ${toWords(num % 1000)}`.trim();
    if (num < 10000000) return `${toWords(Math.floor(num / 100000))} Lakh ${toWords(num % 100000)}`.trim();
    return `${toWords(Math.floor(num / 10000000))} Crore ${toWords(num % 10000000)}`.trim();
  };

  const words = toWords(Math.abs(amount));
  return `${amount < 0 ? "Minus " : ""}${words} Rupees Only`;
};

export const distributePaymentAcrossFeeDetails = (
  selectedFeeDetails = [],
  totalPayment = 0,
) => {
  let remainingPayment = Number(totalPayment) || 0;

  return selectedFeeDetails.map((detail) => {
    const dueAmount = Number(detail.due ?? detail.balance ?? 0);
    const nowPaying = Math.min(dueAmount, remainingPayment);
    remainingPayment = Math.max(0, remainingPayment - nowPaying);

    return {
      ...detail,
      nowPaying,
      remainingBalance: Math.max(0, dueAmount - nowPaying),
    };
  });
};

const FeeCollection = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [lateFeeAmount, setLateFeeAmount] = useState(0);
  const [chequeBounceAmount, setChequeBounceAmount] = useState(0);
  const [reAdmissionAmount, setReAdmissionAmount] = useState(0);
  const [amount, setAmount] = useState(0); // Track the payment amount
  const [balance, setBalance] = useState(0);
  const [bank, setBank] = useState(null);
  const [bankOptions, setBankOptions] = useState([]);

  const [selectedAmount, setSelectedAmount] = useState(0); // State to store the selected "Due" value
  const [amountInput, setAmountInput] = useState("0.00");

  const [lateFee, setLateFee] = useState(0);
  const [reAdmission, setReAdmission] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [chequeBounce, setChequeBounce] = useState(0);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [accountNumberOptions, setAccountNumberOptions] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [selectedFeeDetails, setSelectedFeeDetails] = useState([]);
  const [hostelFeeAmount, setHostelFeeAmount] = useState(0);

  const [totalDues, setTotalDues] = useState(0);
  const [totalOtherCharges, setTotalOtherCharges] = useState(0);
  const [lateFeeEnabled, setLateFeeEnabled] = useState(false);
  const [reAdmissionEnabled, setReAdmissionEnabled] = useState(false);
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [uniquePeriods, setUniquePeriods] = useState([]);
  const [remark, setRemark] = useState("");

  const [chequeBounceEnabled, setChequeBounceEnabled] = useState(false);
  // const [selectedFeeDetails, setSelectedFeeDetails] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [studentBarcode, setStudentBarcode] = useState("");
  const [schoolAdmissionNo, setSchoolAdmissionNo] = useState("");
  const [studentName, setStudentName] = useState("");
  const [className, setClassName] = useState("");
  const [section, setSection] = useState("");
  const [rollNo, setRollNo] = useState("");
  const [feeGroup, setFeeGroup] = useState("");
  const [fetchedId, setFetchedId] = useState(null);

  // const [charges, setCharges] = useState({  lateFee: "",
  // readmission: "",
  // discount: "",
  // chequeBounce: "", });

  const [charges, setCharges] = useState({});

  // const totalDues = selectedFeeDetails.reduce(
  //   (sum, detail) => sum + (detail.balance || 0),
  //   0
  // );

  useEffect(() => {
    // Use original due amount so total stays fixed regardless of prior payments
    const computedTotalDues = selectedFeeDetails.reduce(
      (sum, detail) => sum + (detail.due || 0),
      0
    );
    setTotalDues(computedTotalDues);
  }, [selectedFeeDetails]);

  useEffect(() => {
    const computedTotalOtherCharges = Object.entries(charges).reduce(
      (acc, [key, val]) => {
        if (key === "discount") return acc;
        const value = Math.abs(parseFloat(val || 0));
        return acc + value;
      },
      0
    );
    setTotalOtherCharges(computedTotalOtherCharges);
  }, [charges]);

  const [paidAmount, setPaidAmount] = useState(totalDues + hostelFeeAmount);
  const [isUserInput, setIsUserInput] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [elementNameOptions, setElementNameOptions] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index); // Toggle row expansion
  };
  const isHostelAvailed = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      return ["1", "true", "yes", "y", "t"].includes(
        value.trim().toLowerCase()
      );
    }
    return false;
  };

  const [selectedStudent, setSelectedStudent] = useState({
    studentId: "",
    name: "",
    barcode: "",
    admissionNo: "",
    class: "",
    section: "",
    hostelAvailed: false,
  });

  const [feeDetails, setFeeDetails] = useState([]);

  const [aggregatedFeeDetails, setAggregatedFeeDetails] = useState([]);

  // const [expandedRow, setExpandedRow] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const discountAmount = Math.abs(parseFloat(charges.discount || 0));
  const grossAmount = totalDues + totalOtherCharges;

  const getSelectedFeeAmount = () =>
    selectedFeeDetails.reduce((sum, detail) => {
      const value = Number(detail.due ?? detail.balance ?? 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);

  const handleClear = () => {
    // Reset selections and values
    setSelectedFeeDetails([]);
    setTotalDues(0);
    setTotalOtherCharges(0);
    setSelectedPeriods([]);
    setExpandedRow(null);
    setUniquePeriods([]);
    setAggregatedFeeDetails([]);
    setCharges({});

    setSelectedStudent({
      studentId: "",
      name: "",
      barcode: "",
      admissionNo: "",
      class: "",
      section: "",
      hostelAvailed: false,
    });
    setLateFeeAmount(0);
    setChequeBounceAmount(0);
    setReAdmissionAmount(0);
    setAmount(0);
    setBalance(0);
    setBank(null);
    setBankOptions([]);
    setSelectedBankId(null);
    setAccountNumberOptions([]);
    //  setAccountNumberOptions([]);
    setSelectedAccountId(null);
    setSelectedPaymentMethodId(null);
    setShowModal(false);
    setSelectedPeriod(null);
    setElementNameOptions([]);
    setRollNo("");
    setFeeGroup("");
    setPaidAmount(0);
    setSelectedAmount(0);
    setAmountInput("0.00");
    setBalance(0);
    setHostelFeeAmount(0);
    setIsUserInput(false);

    // Refetch payment methods after clearing
    fetchPaymentMethods();
  };

  // --- Correct handleSelectStudent ---
  const handleSelectStudent = (student) => {
    if (!student) return;

    const studentId = student.studentId || student.id;

    setSelectedStudent({
      id: student.id, // top-level record ID
      studentId: studentId, // actual student ID
      admissionNo: student.school_admission_no || "",
      barcode: student.barcode || "",
      name: student.studentname || "",
      class: student.classname || "",
      section: student.sectionname || "",
      hostelAvailed: isHostelAvailed(
        student.hostel_availed ?? student.hostelAvailed ?? false
      ),
    });

    setSelectedStudentId(studentId);

    localStorage.setItem("selectedClubStudentId", studentId);
    localStorage.setItem("selectedClubRecordId", student.id);

    handleCloseModal();

    fetchStudentData({
      studentClassId: student.id,
      barcode: student.barcode,
      admissionNo: student.school_admission_no,
    });
  };

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}BANK/GetAllbankList/`);
        const result = await response.json();

        if (result.message === "Success") {
          // Map API response to the format React Select needs
          const options = result.data.map((bank) => ({
            value: bank.id, // Bank ID as the value
            label: bank.bankname, // Bank name as the label
          }));
          setBankOptions(options);
        } else {
          console.error("Failed to fetch banks:", result.message);
        }
      } catch (error) {
        console.error("Error fetching banks:", error);
      }
    };

    fetchBanks();
  }, []);

  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!selectedBankId) return; // Do nothing if no bank is selected

      try {
        const response = await fetch(
          `${ApiUrl.apiurl}Account_Details/GetAccountDetailsBasedOnBankId/${selectedBankId}/`
        );
        const result = await response.json();

        if (result.message === "success!!") {
          // Map API response to the format React Select needs
          const options = result.data.map((account) => ({
            value: account.id, // Account ID as the value
            label: `${account.bank_account}`, // Account number as the label
          }));
          setAccountNumberOptions(options);
        } else {
          console.error("Failed to fetch account details:", result.message);
        }
      } catch (error) {
        console.error("Error fetching account details:", error);
      }
    };

    fetchAccountDetails();
  }, [selectedBankId]);

  useEffect(() => {
    const fetchFeeElements = async () => {
      try {
        const response = await fetch(
          `${ApiUrl.apiurl}FEE_ELEMENT_TYPE/GetAllFeeElements/S`
        );
        const result = await response.json();

        if (result.message === "success") {
          const options = result.data.map((element) => ({
            value: element.id, // Store the element ID
            label: element.element_description, // Show element description in dropdown
          }));
          setElementNameOptions(options);
        }
      } catch (error) {
        console.error("Error fetching fee elements:", error);
      }
    };

    fetchFeeElements();
  }, []);

  // Fetch payment methods from API
  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch(
        `${ApiUrl.apiurl}PAYMENTMETHOD/GetAllpaymentmethodList/`
      );
      const result = await response.json();
      if (result.message === "Success" && result.data) {
        // Map data to the format React Select expects
        const options = result.data.map((method) => ({
          value: method.id,
          label: method.paymentmethod,
        }));
        setPaymentMethodOptions(options);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    }
  };
  useEffect(() => {
    fetchPaymentMethods();
  }, []);


  const fetchHostelFeeAmount = async (studentIdValue) => {
    if (!studentIdValue) {
      setHostelFeeAmount(0);
      return 0;
    }

    try {
      const academicyearId = localStorage.getItem("academicSessionId");
      const orgId = localStorage.getItem("orgId");
      const branchId = localStorage.getItem("branchId");
      const queryParams = new URLSearchParams({
        studentId: studentIdValue,
        academicyearId: academicyearId || "",
        org_id: orgId || "",
        branch_id: branchId || "",
      });

      const response = await fetch(
        `${ApiUrl.apiurl}HOSTEL/HostelChargesCalculateBasedOnStudent/?${queryParams.toString()}`
      );
      const data = await response.json();

      if (data.message === "success" && Array.isArray(data.data) && data.data.length > 0) {
        const hostelData = data.data[0];
        const amount = Number(hostelData.total_fees || 0) - Number(hostelData.paid_fees || 0);
        const normalizedAmount = Number.isFinite(amount) ? Math.max(0, amount) : 0;
        setHostelFeeAmount(normalizedAmount);
        return normalizedAmount;
      }

      setHostelFeeAmount(0);
      return 0;
    } catch (error) {
      console.error("Error fetching hostel fee amount:", error);
      setHostelFeeAmount(0);
      return 0;
    }
  };

  const resetHostelFee = () => {
    setHostelFeeAmount(0);
    setSelectedStudent((prev) => ({ ...prev, hostelAvailed: false }));
  };

  const fetchStudentData = async ({
    studentClassId,
    barcode,
    admissionNo,
  } = {}) => {
    if (!studentClassId && !barcode && !admissionNo) return;

    const flag = "y";
    const queryParams = new URLSearchParams();

    if (studentClassId) queryParams.append("id", studentClassId);
    if (barcode) queryParams.append("barcode", barcode);
    if (admissionNo) queryParams.append("schooladmissionno", admissionNo);

    const selectedClubRecordId = localStorage.getItem("selectedClubRecordId");
    const academicSessionId = localStorage.getItem("academicSessionId");

    if (selectedClubRecordId)
      queryParams.append("studentClassId", selectedClubRecordId);
    if (academicSessionId)
      queryParams.append("academic_year_id", academicSessionId);

    queryParams.append("flag", flag);

    const url = `${
      ApiUrl.apiurl
    }FILTER/GetFilterSTUDENTfilterdatabasedoncondition?${queryParams.toString()}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.message === "success!!") {
        const studentData = data.data;
        const hostelAvailed = isHostelAvailed(
          studentData.hostel_availed ?? studentData.hostelAvailed ?? false
        );

        setSelectedStudent((prev) => ({
          ...prev,
          name: studentData.studentname || prev.name,
          class: studentData.classname || prev.class,
          section: studentData.sectionname || prev.section,
          barcode: studentData.barcode || prev.barcode,
          admissionNo: studentData.school_admission_no || prev.admissionNo,
          hostelAvailed,
        }));

        const studentIdFromApi = studentData.id || studentData.studentId;
        setSelectedStudentId(studentIdFromApi);

        setRollNo(studentData.rollno);
        setFeeGroup(studentData.feegroup);
        setFeeDetails(studentData.feedeatls);

        if (studentIdFromApi && hostelAvailed) {
          await fetchHostelFeeAmount(studentIdFromApi);
        } else {
          resetHostelFee();
        }
      } else {
        console.error("Failed to fetch student data:", data.message);
      }
    } catch (error) {
      console.error("Error fetching student data:", error);
    }
  };
  useEffect(() => {
    if (
      selectedStudent?.id ||
      selectedStudent?.barcode ||
      selectedStudent?.admissionNo
    ) {
      fetchStudentData({
        studentClassId: selectedStudent.id,
        barcode: selectedStudent.barcode,
        admissionNo: selectedStudent.admissionNo,
      });
    }
  }, [
    selectedStudent?.id,
    selectedStudent?.barcode,
    selectedStudent?.admissionNo,
  ]);

  const handleMainCheckboxChange = (row, index) => {
    const isSelected = selectedPeriods.includes(index);

    if (isSelected) {
      // Unselect the current period and remove its details
      setSelectedPeriods((previous) => previous.filter((i) => i !== index));
      setSelectedFeeDetails((prev) =>
        prev.filter((detail) => detail.period !== row.period)
      );
    } else {
      // Add the selected period and its details
      const detailsForPeriod = aggregatedFeeDetails
        .filter(
          (detail) =>
            detail.period_month === row.period &&
            parseFloat(detail.balance) > 0 &&
            isMainFeeElement(detail)
        )
        .map((item) => ({
          id: item.id,
          period: row.period,
          element_name: item.element_name,
          paid: parseFloat(item.paid_amount),
          balance: parseFloat(item.balance),
          due: Math.max(
            0,
            parseFloat(item.balance) + parseFloat(item.paid_amount)
          ),
          isChecked: false, // initially unchecked
          period_month_Id: item.period_month_Id, // Include period_month_Id
        }));

      setSelectedPeriods((previous) =>
        previous.includes(index) ? previous : [...previous, index],
      );
      setSelectedFeeDetails((prev) => [
        ...prev.filter((detail) => detail.period !== row.period),
        ...detailsForPeriod,
      ]);
    }
  };

  const handleNestedCheckboxChange = (item, row) => {
    if (!isMainFeeElement(item)) {
      return;
    }

    setSelectedFeeDetails((prev) => {
      const isAlreadySelected = prev.some(
        (detail) =>
          detail.period === row.period &&
          detail.element_name === item.element_name
      );

      if (isAlreadySelected) {
        // Remove item if already selected
        return prev.filter(
          (detail) =>
            !(
              detail.period === row.period &&
              detail.element_name === item.element_name
            )
        );
      } else if (parseFloat(item.balance) > 0) {
        // Add item if not already selected and balance > 0
        return [
          ...prev,
          {
            id: item.id, // Ensure ID is included for backend processing
            period: row.period,
            element_name: item.element_name,
            period_month_Id: item.period_month_Id, // Ensure period_month_Id is included
            paid: parseFloat(item.paid_amount),
            balance: parseFloat(item.balance),
            due: Math.max(
              0,
              parseFloat(item.balance) + parseFloat(item.paid_amount)
            ),
          },
        ];
      }
      return prev;
    });
  };

  // Helper function to group and sum fee details by element_name and period_month
  const aggregateFeeDetails = (feeDetails) => {
    return feeDetails.reduce((acc, item) => {
      const key = `${item.period_month}-${item.element_name}`;
      if (!acc[key]) {
        acc[key] = {
          ...item,
          element_amount: parseFloat(item.element_amount),
          paid_amount: parseFloat(item.paid_amount),
        };
      } else {
        acc[key].element_amount += parseFloat(item.element_amount);
        acc[key].paid_amount += parseFloat(item.paid_amount);
      }
      acc[key].balance = (
        acc[key].element_amount - acc[key].paid_amount
      ).toFixed(2);
      return acc;
    }, {});
  };

  useEffect(() => {
    // Ensure aggregatedFeeDetails is initialized once feeDetails is available
    if (feeDetails && feeDetails.length > 0) {
      setAggregatedFeeDetails(Object.values(aggregateFeeDetails(feeDetails)));
    }
  }, [feeDetails]);

  useEffect(() => {
    fetchStudentData();
  }, []);

  // Helper to get unique periods with summed total and balance amounts
  const getUniquePeriods = (feeDetails) => {
    const periodMap = new Map();

    feeDetails.forEach((detail) => {
      const period = detail.period_month;
      const elementAmount = parseFloat(detail.element_amount) || 0;
      const paidAmount = parseFloat(detail.paid_amount) || 0;

      if (!periodMap.has(period)) {
        periodMap.set(period, {
          period: period,
          totalAmount: 0,
          paidAmount: 0,
          discount: 0,
        });
      }

      const existingData = periodMap.get(period);

      // Keep the period summary consistent with its expanded rows. Persisted
      // charges are part of the period total, while discount rows are kept as
      // a separate adjustment and must not be added to the total.
      if (!isDiscountElement(detail)) {
        existingData.totalAmount += elementAmount;
        existingData.paidAmount += paidAmount;
      }
    });

    return Array.from(periodMap.values())
      .map((item) => ({
        ...item,
        totalAmount: item.totalAmount.toFixed(2),
        paidAmount: item.paidAmount.toFixed(2),
        discount: item.discount.toFixed(2),
        balanceAmount: Math.max(0, item.totalAmount - item.paidAmount).toFixed(2),
      }))
      .sort((first, second) => {
        const firstSortKey = getFeePeriodSortKey(first.period);
        const secondSortKey = getFeePeriodSortKey(second.period);
        const orderDifference = firstSortKey.order - secondSortKey.order;

        if (orderDifference !== 0) return orderDifference;
        if (firstSortKey.yearly !== secondSortKey.yearly) {
          return firstSortKey.yearly - secondSortKey.yearly;
        }
        return String(first.period || "").localeCompare(String(second.period || ""));
      });
  };

  // const uniquePeriods = getUniquePeriods(feeDetails);

  useEffect(() => {
    // Get unique periods from feeDetails
    const uniquePeriodsData = getUniquePeriods(feeDetails);
    setUniquePeriods(uniquePeriodsData);
  }, [feeDetails]);

  const navigate = useNavigate();

  const dateRef = useRef(null);
  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);


  const getComputedPaymentAmount = () => {
    const selectedBaseAmount = isUserInput
      ? Math.max(0, Number(selectedAmount || 0))
      : selectedFeeDetails.length > 0
        ? getSelectedFeeAmount()
        : Math.max(0, totalDues);

    const selectedGrossAmount = selectedBaseAmount + totalOtherCharges;
    return Math.max(0, selectedGrossAmount - discountAmount);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCharges((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (!isUserInput) {
      const autoPayable = getComputedPaymentAmount();
      setPaidAmount(autoPayable);
      setSelectedAmount(autoPayable);
      setAmountInput(formatAmountInput(autoPayable));
    } else {
      setPaidAmount(selectedAmount);
      setAmountInput(formatAmountInput(selectedAmount));
    }
  }, [charges, grossAmount, selectedFeeDetails, totalDues, hostelFeeAmount, isUserInput, selectedAmount]);

  useEffect(() => {
    // selectedDate is already initialized to new Date() in state
    // No need to manually set it via ref
  }, []);

  const handleCloseButton = () => {
    navigate("/admin/fee-search");
  };

  useEffect(() => {
    if (studentBarcode || schoolAdmissionNo || studentName) {
      fetchStudentData();
    }
  }, [studentBarcode, schoolAdmissionNo, studentName]);

  // Trigger the API call if any identifier is available in selectedStudent
  useEffect(() => {
    if (
      selectedStudent?.id ||
      selectedStudent?.barcode ||
      selectedStudent?.admissionNo
    ) {
      fetchStudentData({
        id: selectedStudent.id,
        barcode: selectedStudent.barcode,
        admissionNo: selectedStudent.admissionNo,
      });
    }
  }, [
    selectedStudent?.id,
    selectedStudent?.barcode,
    selectedStudent?.admissionNo,
  ]);

  const formatAmountInput = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return String(value).trim();
    return Number(numericValue).toFixed(2);
  };

  // Handler for updating the amount field
  const handleAmountChange = (event) => {
    const rawValue = event.target.value;
    if (rawValue === "") {
      setIsUserInput(true);
      setAmountInput("");
      setSelectedAmount(0);
      setPaidAmount(0);
      return;
    }

    const sanitizedValue = rawValue
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");

    if (!sanitizedValue) {
      return;
    }

    setIsUserInput(true);
    setAmountInput(sanitizedValue);

    const value = Number.parseFloat(sanitizedValue);
    if (!Number.isNaN(value)) {
      const safeValue = Math.max(0, value);
      setSelectedAmount(safeValue);
      setPaidAmount(safeValue);
    }
  };

  // Calculate balance when gross amount or paid amount changes
  useEffect(() => {
    setBalance(Math.max(0, grossAmount - discountAmount - paidAmount));
  }, [grossAmount, discountAmount, paidAmount]);

  const handlePaidAmountChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      setIsUserInput(true);
      setPaidAmount(0);
      return;
    }

    const newPaidAmount = Number.parseFloat(rawValue);
    if (Number.isNaN(newPaidAmount)) return;

    setIsUserInput(true);
    setPaidAmount(newPaidAmount);
  };




const handleSave = async () => {
  const apiUrl = `${ApiUrl.apiurl}FEESRECIPTS/feeReciptsCreate/`;

  const academicyearId = localStorage.getItem("academicSessionId");
  const loginId = sessionStorage.getItem("userId");

  if (!academicyearId || !loginId) {
    alert("Required session or local storage data is missing.");
    return;
  }

  if (!selectedStudentId) {
    alert("Student data is missing. Please fetch the student data first.");
    return;
  }

  if (!selectedDate) {
    alert("Please select a Reference Date.");
    return;
  }

  if (!selectedPaymentMethodId) {
    alert("Please select a Payment Method.");
    return;
  }

  let accountnumber = null;

  if (selectedPaymentMethodId === 2) {
    if (!selectedBankId) {
      alert("Please select a Bank.");
      return;
    }

    const selectedAccountOption = accountNumberOptions.find(
      (option) => option.value === selectedAccountId
    );
    accountnumber = selectedAccountOption ? selectedAccountOption.label : "";

    if (!accountnumber) {
      alert("Please select a valid Account Number.");
      return;
    }
  }

  const stdfeedetailsIds = selectedFeeDetails.map((detail) => detail.id);
  const periodMonths = [
    ...new Set(selectedFeeDetails.map((detail) => detail.period_month_Id)),
  ];

  if (!periodMonths || periodMonths.length === 0) {
    alert("Fee Period is required. Please select at least one fee period.");
    return;
  }

  const userConfirmed = window.confirm(
    "Are you sure you want to create and approve the Fee Receipt?"
  );
  if (!userConfirmed) return;

  const formattedReferenceDate = selectedDate.toLocaleDateString("en-CA");
  const discountValue = Math.abs(parseFloat(charges.discount || 0));

  const chargeLabelMap = {
    latefee: "Late Fee",
    readmission: "ReAdmission",
    discount: "DISCOUNT",
    chequebounce: "Cheque Bounce",
  };

  const distributedRows = distributePaymentAcrossFeeDetails(
    selectedFeeDetails,
    paidAmount,
  );

  const feeElementMap = distributedRows.reduce((acc, item) => {
    const key = item.element_name;
    const paidValue = Number(item.nowPaying || 0);
    if (paidValue > 0) {
      acc[key] = (acc[key] || 0) + paidValue;
    }
    return acc;
  }, {});

  const feeElementRows = Object.entries(feeElementMap)
    .filter(([, value]) => value !== 0)
    .map(([element_name, value]) => ({ element_name, amount: value }));

  // Do NOT add a separate hostel fee row — hostel is already in selectedFeeDetails
  const otherChargeRows = Object.entries(charges)
    .filter(([, value]) => Math.abs(parseFloat(value || 0)) > 0)
    .map(([key, value]) => {
      const amountValue = Math.abs(parseFloat(value || 0));
      return {
        element_name: chargeLabelMap[key] || key,
        amount: key === "discount" ? -amountValue : amountValue,
      };
    });

  const frontendReceiptElements = [...feeElementRows, ...otherChargeRows];

  const payload = {
    loginId: parseInt(loginId),
    studentId: selectedStudentId,
    academicyearId: parseInt(academicyearId),
    reciptdate: formattedReferenceDate,
    paymentmethodId: selectedPaymentMethodId,
    bankId: selectedPaymentMethodId === 2 ? selectedBankId : null,
    accountnumber: selectedPaymentMethodId === 2 ? accountnumber : null,
    remarks: remark,
    remark: remark,
    referencedate: formattedReferenceDate,
    stdfeedetailsIds,
    period_month: periodMonths,
    latefees: parseFloat(charges.latefee || 0),
    // Backend expects discount as a positive number and applies the sign internally.
    discountfees: discountValue,
    checkbouncefees: parseFloat(charges.chequebounce || 0),
    readmissionfees: parseFloat(charges.readmission || 0),
    totalamount: parseFloat(paidAmount || 0),
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error(`Error: ${response.status}`);

    const result = await response.json();
    console.log("Fee receipt created successfully:", result);
    alert("Fee receipt created successfully!");

    // Generate PDF instead of HTML popup
    await generatePDF(result.receipt_data, frontendReceiptElements, paidAmount);

    handleClear();
  } catch (error) {
    console.error("Error creating fee receipt:", error);
    alert("Failed to create fee receipt. Please try again.");
  }
};

// ---------------- PDF GENERATION FUNCTION ----------------
const generatePDF = async (data, frontendReceiptElements = [], fallbackPaidAmount = 0) => {
  const doc = new jsPDF("portrait", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();

  // Always use frontend-computed elements — server data has accumulated historical values
  const sourceItems = frontendReceiptElements;

  const feeRows = sourceItems
    .filter((item) => Number(item.amount || 0) !== 0)
    .map((item, index) => [
      index + 1,
      safeText(item.element_name || "").toUpperCase(),
      Number(item.amount || 0).toFixed(2),
    ]);

  const totalAmount = Number(data?.amount ?? fallbackPaidAmount ?? 0);

  if (feeRows.length > 0) {
    feeRows.push(["", "TOTAL", totalAmount.toFixed(2)]);
  }

  doc.setLineWidth(0.5);
  doc.rect(10, 6, pageWidth - 20, 286);

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text(
    "SPARSH COLLEGE OF NURSING & ALLIED SCIENCES",
    pageWidth / 2,
    16,
    { align: "center" },
  );

  doc.setFontSize(8);
  doc.setFont("Helvetica", "normal");
  doc.text("(A unit of Nirmala Trust)", pageWidth / 2, 21, { align: "center" });
  doc.text(
    "Plot No: 154/1683/2194 & 177/2195, Kantiabada, Bhubaneswar-752054",
    pageWidth / 2,
    25,
    { align: "center" },
  );
  doc.text(
    "Ph.: +91 7735504783, Email: info@sparshnursing.edu.in",
    pageWidth / 2,
    29,
    { align: "center" },
  );

  doc.setFillColor(0, 120, 90);
  doc.rect(88, 34, 32, 8, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(10);
  doc.text("RECEIPT", 104, 40, { align: "center" });

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont("Helvetica", "normal");
  doc.text(`Receipt No. ${safeText(data.receipt_no)}`, 130, 35);
  doc.text(`Date: ${formatReceiptDate(data.receipt_date)}`, 130, 41);
  doc.text(`Course: ${safeText(data.classname)}`, 15, 49);
  doc.text(`Year / Sem: ${safeText(data.feeperiods)}`, 80, 49);
  doc.text(`Adm No.: ${safeText(data.admission_no)}`, 150, 49);
  doc.text(`Department: Nursing`, 15, 56);
  doc.text(`Section: ${safeText(data.sectionname)}`, 120, 56);

  doc.text("Received from Ms./Mr.:", 15, 63);
  doc.setFont("Helvetica", "bold");
  doc.text(safeText(data.studentname), 62, 63);
  doc.setFont("Helvetica", "normal");
  doc.text(`Father Name: ${safeText(data.fathername)}`, 15, 70);
  doc.text(`Payment Mode: ${safeText(data.payment_method)}`, 120, 70);

  doc.setDrawColor(0, 0, 0);
  doc.rect(15, 76, 180, 118);
  doc.line(15, 84, 195, 84);
  doc.line(15, 76, 15, 194);
  doc.line(25, 76, 25, 194);
  doc.line(155, 76, 155, 194);
  doc.line(183, 76, 183, 194);
  doc.line(195, 76, 195, 194);

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(9);
  doc.text("SL NO", 18, 82);
  doc.text("PARTICULARS", 40, 82);
  doc.text("AMOUNT", 173, 82, { align: "center" });
  doc.setFont("Helvetica", "normal");

  const rowTop = 90;
  const rowHeight = 7;

  feeRows.forEach(([sl, label, amountStr], idx) => {
    const y = rowTop + idx * rowHeight;
    doc.setFont("Helvetica", idx === feeRows.length - 1 ? "bold" : "normal");
    doc.text(String(sl || idx + 1), 18, y + 4);
    doc.text(label, 30, y + 4);
    doc.text(amountStr, 175, y + 4, { align: "right" });
  });

  doc.setFont("Helvetica", "bold");
  doc.text("TOTAL", 130, 205);
  doc.text(totalAmount.toFixed(2), 175, 205, { align: "right" });

  doc.setFont("Helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Amount in words: ${amountToWords(totalAmount)}`, 15, 216);
  doc.text(`Remarks: ${safeText(data.remarks || data.payment_reference || "")}`, 15, 222);

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Authorization Signature", 155, 246, { align: "center" });

  const pdfBlob = doc.output("blob");
  const blobUrl = URL.createObjectURL(pdfBlob);
  window.open(blobUrl, "_blank");
};

  return (
    <div className="container-fluid ">
      <div className="row">
        <div className="col-12">
          <div className="card p-0">
            <div className="card-body">
              <p
                style={{
                  marginBottom: "0px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                FEE COLLECTION
              </p>
              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary me-2"
                    style={{
                      width: "150px",
                    }}
                    onClick={handleSave}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    style={{
                      width: "150px",
                    }}
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger me-2"
                    style={{
                      width: "150px",
                    }}
                    onClick={handleCloseButton}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="row mt-3 mx-2 mb-4 ">
                <div className="col-12 custom-section-box">
                  <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                    <div className="row flex-grow-1">
                      <div className="col-12 col-md-3 mb-3">
                        <label
                          htmlFor="date"
                          className="form-label d-flex align-items-center"
                        >
                          Date
                        </label>
                        <div className="fee-collection-date-field">
                          <DatePicker
                            selected={selectedDate}
                            onChange={(date) => setSelectedDate(date)}
                            className="form-control detail w-100 fee-collection-date-input"
                            wrapperClassName="w-100"
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Select Date"
                          />
                          <FontAwesomeIcon
                            icon={faCalendarAlt}
                            className="fee-collection-date-icon"
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-3 mb-3">
                        <label htmlFor="student-barcode" className="form-label">
                          Student BarCode
                        </label>
                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            id="student-barcode"
                            className="form-control detail fee-field-control"
                            placeholder="Enter student barcode"
                            value={selectedStudent.barcode || ""}
                            onChange={(e) =>
                              setSelectedStudent((prev) => ({
                                ...prev,
                                barcode: e.target.value,
                              }))
                            }
                            onBlur={fetchStudentData}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-3 mb-1">
                        <label htmlFor="student-name" className="form-label">
                          Student Name
                        </label>
                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            id="student-name"
                            className="form-control detail"
                            placeholder="Enter student name"
                            value={selectedStudent.name || ""}
                            onChange={(e) =>
                              setSelectedStudent((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            onBlur={fetchStudentData}
                          />
                          <button
                            type="button"
                            className="btn btn-primary ms-2 mb-0 mt-0"
                            onClick={handleOpenModal}
                            style={{ width: "50px", padding: "3px" }}
                          >
                            ...
                          </button>
                        </div>
                      </div>
                      <SelectStudentFeeModal
                        show={showModal}
                        handleClose={handleCloseModal}
                        onSelectStudent={handleSelectStudent}
                      />

                      <div className="col-12 col-md-3 mb-1">
                        <label htmlFor="class" className="form-label">
                          Class
                        </label>
                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            id="class"
                            className="form-control detail"
                            placeholder="Enter class"
                            value={selectedStudent.class || ""}
                            onChange={(e) =>
                              setSelectedStudent((prev) => ({
                                ...prev,
                                class: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-3 mb-1">
                        <label htmlFor="roll-no" className="form-label">
                          Roll No
                        </label>
                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            id="roll-no"
                            className="form-control detail"
                            placeholder="Enter roll no"
                            value={rollNo || ""}
                            onChange={(e) => setRollNo(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-3 mb-1">
                        <label htmlFor="section" className="form-label">
                          Section
                        </label>
                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            id="section"
                            className="form-control detail"
                            placeholder="Enter section"
                            value={selectedStudent.section || ""}
                            onChange={(e) =>
                              setSelectedStudent((prev) => ({
                                ...prev,
                                section: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-3 mb-4">
                        <label htmlFor="fee-group" className="form-label">
                          Fee Group
                        </label>
                        <Select
                          id="fee-group"
                          className="detail"
                          value={{ label: feeGroup, value: feeGroup }}
                          options={[]}
                          placeholder="Select Fee Group"
                          // classNamePrefix="react-select"
                          isDisabled={false} // Set to false to allow selection if needed
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mx-2">
                <div className="col-md-6" style={{ border: "1px solid #ccc" }}>
                  <h4>Fee Period Details</h4>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="table-header">
                        <tr>
                          <th></th>
                          <th>Period</th>
                          <th>Total Amount</th>
                          <th>Paid Amount</th>
                          <th>Discount</th>
                          <th>Balance Amount</th>
                          <th>Pay</th>
                        </tr>
                      </thead>

                      <tbody>
                        {uniquePeriods.map((row, index) => {
                          // Selecting a period is only a pending payment selection.
                          // Paid and balance values change after Save and a fresh API response.
                          const displayedPaidAmount = Number(row.paidAmount || 0);
                          const balanceAmount = Number(row.balanceAmount || 0);
                          const isPeriodSelected = selectedPeriods.includes(index);

                          return (
                          <React.Fragment key={index}>
                            <tr
                              className="highlight"
                              onClick={() => handleRowClick(index)}
                            >
                              <td>
                                <FaPlus style={{ cursor: "pointer" }} />
                              </td>
                              <td>{row.period}</td>
                              <td>{row.totalAmount}</td>
                              <td>{displayedPaidAmount.toFixed(2)}</td>
                              <td>{row.discount}</td>
                              <td>{balanceAmount.toFixed(2)}</td>
                              <td>
                                {balanceAmount > 0 || isPeriodSelected ? (
                                  <input
                                    type="checkbox"
                                    checked={isPeriodSelected}
                                    onChange={() =>
                                      handleMainCheckboxChange(row, index)
                                    }
                                  />
                                ) : null}
                              </td>
                            </tr>
                            {expandedRow === index && (
                              <tr>
                                <td colSpan="7">
                                  <div className="table-responsive">
                                    <table className="table table-bordered">
                                      <thead>
                                        <tr>
                                          <th>Element Name</th>
                                          <th>Amount</th>
                                          <th>Paid</th>
                                          <th>Balance</th>
                                          <th>Pay</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {aggregatedFeeDetails
                                          .filter(
                                            (detail) =>
                                              detail.period_month ===
                                              row.period,
                                          )
                                          .map((item, subIndex) => {
                                            const nestedPaidAmount = Number(
                                              item.paid_amount || 0,
                                            );
                                            const nestedBalanceAmount = Number(
                                              item.balance || 0,
                                            );

                                            return (
                                            <tr key={subIndex}>
                                              <td>{item.element_name}</td>
                                              <td>
                                                {item.element_amount.toFixed(2)}
                                              </td>
                                              <td>
                                                {nestedPaidAmount.toFixed(2)}
                                              </td>
                                              <td>{nestedBalanceAmount.toFixed(2)}</td>
                                              <td>
                                                {parseFloat(item.balance) > 0 && isMainFeeElement(item) ? (
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedFeeDetails.some(
                                                      (detail) =>
                                                        detail.period ===
                                                          row.period &&
                                                        detail.element_name ===
                                                          item.element_name,
                                                    )}
                                                    onChange={() =>
                                                      handleNestedCheckboxChange(
                                                        item,
                                                        row,
                                                      )
                                                    }
                                                  />
                                                ) : null}
                                              </td>
                                            </tr>
                                            );
                                          })}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-md-6" style={{ border: "1px solid #ccc" }}>
                  <h4>Fee Paid Details</h4>
                  <div className="table-responsive">
                    <table className="table table-bordered ">
                      <thead className="table-header">
                        <tr>
                          <th>Fee Period</th>
                          <th>Fee Heads</th>
                          <th>Due</th>
                          <th>Paid</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const distributedRows = distributePaymentAcrossFeeDetails(
                            selectedFeeDetails,
                            Number(paidAmount || 0),
                          );

                          return distributedRows.map((detail, idx) => {
                            const dueAmount = Number(detail.due ?? detail.balance ?? 0);
                            const nowPaying = Number(detail.nowPaying || 0);
                            const remaining = Math.max(0, dueAmount - nowPaying);

                            return (
                              <tr key={`${detail.element_name || "fee"}-${idx}`}>
                                <td>{detail.period}</td>
                                <td>{detail.element_name}</td>
                                <td>{Number(detail.due || 0).toFixed(2)}</td>
                                <td>{nowPaying.toFixed(2)}</td>
                                <td>{remaining.toFixed(2)}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="row mx-2">
                <div className="col-md-6" style={{ border: "1px solid #ccc" }}>
                  <h4>Fee Details</h4>
                  <div className="table-responsive">
                    <table className="table table-bordered ">
                      <thead className="table-header">
                        <tr>
                          <th>Period</th>
                          <th>Fee Heads</th>
                          <th>Due</th>
                          <th>Paid</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniquePeriods
                          .filter(
                            (row) => row.paidAmount >= row.totalAmount / 2,
                          ) // Include fully paid and half-paid periods
                          .map((filteredRow, index) => (
                            <React.Fragment key={index}>
                              {aggregatedFeeDetails
                                .filter(
                                  (detail) =>
                                    detail.period_month === filteredRow.period,
                                )
                                .map((item, subIndex) => (
                                  <tr key={subIndex}>
                                    <td>{filteredRow.period}</td>
                                    <td>{item.element_name}</td>
                                    <td>{item.element_amount}</td>
                                    <td>{item.paid_amount.toFixed(2)}</td>
                                    <td>{item.balance}</td>
                                  </tr>
                                ))}
                            </React.Fragment>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-md-6" style={{ border: "1px solid #ccc" }}>
                  <h4>Payment Details</h4>
                  <form className="row g-3">
                    {/* Payment Method */}
                    <div className="col-md-3">
                      <label htmlFor="paymentMethod" className="form-label">
                        Payment Method
                      </label>

                      <Select
                        id="paymentMethod"
                        className="detail"
                        options={paymentMethodOptions}
                        classNamePrefix="react-select"
                        placeholder="Select Method"
                        value={
                          paymentMethodOptions.find(
                            (option) =>
                              option.value === selectedPaymentMethodId,
                          ) || null
                        }
                        onChange={(selectedOption) => {
                          const newValue = selectedOption
                            ? selectedOption.value
                            : null;
                          setSelectedPaymentMethodId(newValue);

                          // Clear Bank and Account fields if switching to CASH (id === 1)
                          if (newValue === 1) {
                            setSelectedBankId(null);
                            setSelectedAccountId(null);
                          }
                        }}
                      />
                    </div>

                    {/* Bank Name */}
                    <div className="col-md-3">
                      <label htmlFor="bankName" className="form-label">
                        Bank Name
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        className="detail"
                        options={bankOptions}
                        placeholder="Select Bank"
                        value={
                          selectedBankId
                            ? bankOptions.find(
                                (option) => option.value === selectedBankId,
                              )
                            : null
                        }
                        onChange={(selectedOption) =>
                          setSelectedBankId(
                            selectedOption ? selectedOption.value : null,
                          )
                        }
                        isDisabled={selectedPaymentMethodId === 1}
                      />
                    </div>

                    {/* Account Name */}
                    <div className="col-md-3 ">
                      <label htmlFor="accountName" className="form-label">
                        Account Number
                      </label>
                      <Select
                        id="accountName"
                        className="detail"
                        options={accountNumberOptions}
                        classNamePrefix="react-select"
                        placeholder="Select Account"
                        isDisabled={selectedPaymentMethodId === 1}
                        value={
                          selectedAccountId
                            ? accountNumberOptions.find(
                                (option) => option.value === selectedAccountId,
                              )
                            : null
                        }
                        onChange={(selectedOption) =>
                          setSelectedAccountId(
                            selectedOption ? selectedOption.value : null,
                          )
                        }
                      />
                    </div>

                    {/* Reference Date */}
                    <div className="col-md-3">
                      <label htmlFor="referenceDate" className="form-label">
                        Reference Date
                      </label>
                      <div className="fee-collection-date-field">
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => setSelectedDate(date)}
                          dateFormat="yyyy/MM/dd"
                          className="form-control detail w-100 fee-collection-date-input"
                          wrapperClassName="w-100"
                          id="referenceDate"
                        />
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="fee-collection-date-icon"
                        />
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="col-md-3 mb-3">
                      <label htmlFor="amount" className="form-label">
                        Amount
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control detail fee-collection-amount-input"
                        id="amount"
                        value={amountInput}
                        onChange={handleAmountChange}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (
                            [
                              "ArrowUp",
                              "ArrowDown",
                              "e",
                              "E",
                              "+",
                              "-",
                            ].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.preventDefault()}
                      />
                    </div>

                    {/* Reference */}
                    <div className="col-md-3">
                      <label htmlFor="reference" className="form-label">
                        Remarks
                      </label>
                      <input
                        type="text"
                        className="form-control detail"
                        id="remark"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                      />
                    </div>
                  </form>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6"></div>

                {/* <!-- Other Charges --> */}
                <div class="col-md-3" style={{ border: "1px solid #ccc" }}>
                  <h4>Other Charges</h4>
                  <form>
                    <div className="table-responsive">
                      <table className="table table-bordered ">
                        <tbody>
                          {[
                            "Late Fee",
                            "ReAdmission",
                            "Discount",
                            "Cheque Bounce",
                          ].map((label, index) => {
                            const fieldName = label
                              .toLowerCase()
                              .replace(/ /g, ""); // 'latefee', 'readmission', 'discount', 'chequebounce'

                            const handleCheckboxChange = (event) => {
                              const isChecked = event.target.checked;
                              if (isChecked) {
                                // Set it to an empty string initially
                                setCharges((prev) => ({
                                  ...prev,
                                  [fieldName]: prev[fieldName] ?? "",
                                }));
                              } else {
                                // Remove the property entirely
                                setCharges((prev) => {
                                  const copy = { ...prev };
                                  delete copy[fieldName];
                                  return copy;
                                });
                              }
                            };

                            return (
                              <tr key={index}>
                                <td className="align-middle">{label}</td>
                                <td className="d-flex align-items-center">
                                  <input
                                    type="checkbox"
                                    className="me-2"
                                    checked={charges.hasOwnProperty(fieldName)}
                                    onChange={handleCheckboxChange}
                                  />
                                  <input
                                    type="text"
                                    name={fieldName}
                                    className="form-control form-control-sm w-auto form-control detail"
                                    value={charges[fieldName] ?? ""}
                                    onChange={handleChange}
                                    disabled={
                                      !charges.hasOwnProperty(fieldName)
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </form>
                </div>

                {/* <!-- Totals --> */}
                <div class="col-md-3" style={{ border: "1px solid #ccc" }}>
                  <h4>Totals</h4>
                  <div className="table-responsive">
                    <table className="table table-bordered ">
                      <tbody>
                        <tr>
                          <td>Total Dues</td>
                          <td>
                            <input
                              type="text"
                              className="form-control detail"
                              value={totalDues.toFixed(2)} // Total Dues
                              readOnly
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>Total Other Charges</td>
                          <td>
                            <input
                              type="text"
                              className="form-control detail"
                              value={totalOtherCharges.toFixed(2)}
                              readOnly
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>Gross Amount</td>
                          <td>
                            <input
                              type="text"
                              className="form-control detail"
                              value={grossAmount.toFixed(2)}
                              readOnly
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>Paid Amount</td>
                          <td>
                            <input
                              type="text"
                              className="form-control detail"
                              value={paidAmount.toFixed(2)}
                              onChange={handlePaidAmountChange} // Paid Amount
                              // value={(totalDues + totalOtherCharges).toFixed(2)}
                              readOnly
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>Balance</td>
                          <td>
                            <input
                              type="text"
                              className="form-control detail"
                              value={balance.toFixed(2)} // Balance (Gross Amount - Paid Amount)
                              readOnly
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeCollection;



